/**
 * Whether a monster's tells — rim light and HP bar — should show (2026-10-16).
 *
 * QA #11 widened how far monsters are *streamed* (`NOISE.MONSTER_VIEW_RADIUS_M`,
 * 16m) so they stop popping in at the edge of the frame. Streaming is not
 * seeing: design/gdd/lighting.md Core Rule 6 still says an unlit body is seen
 * at 4m and a lit one at 10m. The body itself is shaded by the scene's real
 * lights, so a skeleton in the dark reads as dark on its own — but the rim is
 * additive and the HP bar ignores depth, and either one would pick it out of
 * the dark at 16m. They show only where the server's sight rule would.
 *
 * Mirrors the server's `isLit` / `sightRadiusFor` (tuning.ts `LIGHTING`,
 * `NOISE`) minus the line-of-sight test: the server only streams a body it
 * has line of sight to, so that half is already applied.
 */
export const SIGHT_RADIUS_DARK_M = 4;
export const SIGHT_RADIUS_LIT_M = 10;
export const BRAZIER_LIGHT_RADIUS_M = 6;
export const LANTERN_LIGHT_RADIUS_M = 4;
/** Released this much further out than acquired, so a body on the boundary does not flicker. */
export const REVEAL_HYSTERESIS_M = 0.5;

export interface LightProbe {
  x: number;
  z: number;
}

/** True if any lit brazier or lit lantern reaches `pos` — `isLit` on the server, without its LOS term. */
export function isLitAt(
  pos: LightProbe,
  litBraziers: Iterable<LightProbe>,
  lanterns: Iterable<LightProbe>,
): boolean {
  for (const b of litBraziers) if (Math.hypot(b.x - pos.x, b.z - pos.z) <= BRAZIER_LIGHT_RADIUS_M) return true;
  for (const l of lanterns) if (Math.hypot(l.x - pos.x, l.z - pos.z) <= LANTERN_LIGHT_RADIUS_M) return true;
  return false;
}

export function monsterRevealed(
  monster: LightProbe, observer: LightProbe, lit: boolean, wasRevealed: boolean,
): boolean {
  const radius = (lit ? SIGHT_RADIUS_LIT_M : SIGHT_RADIUS_DARK_M) + (wasRevealed ? REVEAL_HYSTERESIS_M : 0);
  return Math.hypot(monster.x - observer.x, monster.z - observer.z) <= radius;
}
